// SafetyPage.js — HSE Manual quick-reference + scope of work
// Route: #safety?project=BASTROP — no login required
// Collapsible cards from LYT HSE Manual v2.3, emergency contacts, Spanish support
import React, { useState } from 'react';
import {
  ArrowLeft, Shield, Phone, ChevronDown, ChevronUp,
  HardHat, Shovel, Globe, Download, AlertTriangle,
  Zap, Eye, Thermometer, FileText, Construction
} from 'lucide-react';
import { useTranslation } from '../config/translations';
import { useMobile } from '../utils/theme';

const SafetyPage = ({ darkMode, setCurrentPage }) => {
  const { t, lang, toggleLang } = useTranslation();
  const isMobile = useMobile();

  const bgColor = darkMode ? '#0a1628' : '#f0f4f8';
  const cardBg = darkMode ? '#0d1b2a' : '#ffffff';
  const textColor = darkMode ? '#ffffff' : '#1e293b';
  const mutedColor = darkMode ? 'rgba(255,255,255,0.6)' : '#6b7280';
  const borderColor = darkMode ? '#374151' : '#e5e7eb';
  const accentPrimary = darkMode ? '#667eea' : '#00b4d8';
  const dangerColor = '#ef4444';
  const tealColor = darkMode ? '#06b6d4' : '#0d9488';

  // Parse URL params
  const getUrlParams = () => {
    try {
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.split('?')[1] || '');
      return { project: params.get('project') || 'BASTROP', from: params.get('from') || null };
    } catch { return { project: 'BASTROP', from: null }; }
  };
  const { project, from } = getUrlParams();

  const goBack = () => {
    if (from === 'hub') {
      window.location.hash = `#field-hub?project=${project}`;
    } else {
      setCurrentPage('admin-dashboard');
    }
  };

  // Collapsible section state
  const [expanded, setExpanded] = useState({});
  const toggle = (key) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  const sections = [
    { key: 'ppe', icon: HardHat, title: t('safety_ppe'), desc: t('safety_ppe_desc'), color: '#3b82f6' },
    { key: 'trench', icon: Shovel, title: t('safety_trench'), desc: t('safety_trench_desc'), color: '#a855f7' },
    { key: 'hdd', icon: Construction, title: t('safety_hdd'), desc: t('safety_hdd_desc'), color: '#f97316' },
    { key: 'aerial', icon: Zap, title: t('safety_aerial'), desc: t('safety_aerial_desc'), color: '#eab308' },
    { key: 'traffic', icon: AlertTriangle, title: t('safety_traffic'), desc: t('safety_traffic_desc'), color: '#ef4444' },
    { key: 'electrical', icon: Zap, title: t('safety_electrical'), desc: t('safety_electrical_desc'), color: '#f43f5e' },
    { key: 'fiber', icon: Eye, title: t('safety_fiber'), desc: t('safety_fiber_desc'), color: '#06b6d4' },
    { key: 'emergency_proc', icon: Phone, title: t('safety_emergency_proc'), desc: t('safety_emergency_desc'), color: '#dc2626' },
    { key: 'incident', icon: FileText, title: t('safety_incident'), desc: t('safety_incident_desc'), color: '#8b5cf6' },
    { key: 'heat', icon: Thermometer, title: t('safety_heat'), desc: t('safety_heat_desc'), color: '#f97316' },
  ];

  const emergencyContacts = [
    { label: lang === 'es' ? 'Emergencias' : 'Emergency', number: '911', color: dangerColor },
    { label: lang === 'es' ? 'Antes de Excavar' : 'Call Before You Dig', number: '811', color: '#f97316' },
    { label: lang === 'es' ? 'Seguridad LYT (Matt)' : 'LYT Safety (Matt)', number: '(281) 832-8058', color: tealColor },
  ];

  return (
    <div style={{ minHeight: '100vh', backgroundColor: bgColor, padding: isMobile ? '16px 12px' : '24px 16px' }}>
      <div style={{ maxWidth: '700px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
          <button onClick={goBack} style={{
            display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px',
            backgroundColor: 'transparent', border: `1px solid ${borderColor}`,
            borderRadius: '8px', color: textColor, cursor: 'pointer', fontSize: '0.85rem',
          }}>
            <ArrowLeft size={16} /> {from === 'hub' ? (lang === 'es' ? 'Volver al Centro' : 'Back to Hub') : (lang === 'es' ? 'Atras' : 'Back')}
          </button>
          <div style={{ flex: 1 }}>
            <h1 style={{
              fontSize: isMobile ? '1.2rem' : '1.5rem', fontWeight: '700', color: textColor,
              margin: 0, display: 'flex', alignItems: 'center', gap: '8px',
            }}>
              <Shield size={22} style={{ color: tealColor }} />
              {t('safety_title')}
            </h1>
            <p style={{ color: mutedColor, margin: '2px 0 0', fontSize: '0.8rem' }}>
              {t('safety_subtitle')}
            </p>
          </div>
          <button onClick={toggleLang} style={{
            display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 14px',
            backgroundColor: darkMode ? '#1e293b' : '#ffffff', border: `1px solid ${borderColor}`,
            borderRadius: '20px', color: textColor, cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600',
          }}>
            <Globe size={14} /> {lang === 'en' ? 'ES' : 'EN'}
          </button>
        </div>

        {/* Emergency Contacts — always visible */}
        <div style={{
          backgroundColor: darkMode ? '#2a1a1a' : '#fef2f2',
          borderRadius: '12px', padding: '16px 20px', marginBottom: '16px',
          border: `2px solid ${dangerColor}40`,
        }}>
          <h3 style={{
            fontSize: '0.9rem', fontWeight: '700', color: dangerColor, margin: '0 0 12px',
            display: 'flex', alignItems: 'center', gap: '8px', textTransform: 'uppercase', letterSpacing: '0.5px',
          }}>
            <Phone size={16} /> {t('safety_emergency')}
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '10px' }}>
            {emergencyContacts.map((c, i) => (
              <a
                key={i}
                href={`tel:${c.number.replace(/[^0-9+]/g, '')}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  padding: '12px 16px', borderRadius: '10px',
                  backgroundColor: darkMode ? '#1a1a2a' : '#ffffff',
                  border: `1px solid ${c.color}40`,
                  textDecoration: 'none', cursor: 'pointer',
                }}
              >
                <div style={{
                  width: '36px', height: '36px', borderRadius: '8px',
                  backgroundColor: c.color + '20', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Phone size={16} style={{ color: c.color }} />
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', color: mutedColor }}>{c.label}</div>
                  <div style={{ fontSize: '1rem', fontWeight: '700', color: c.color }}>{c.number}</div>
                </div>
              </a>
            ))}
          </div>
        </div>

        {/* HSE Quick Reference Cards */}
        {sections.map((section) => {
          const Icon = section.icon;
          const isOpen = expanded[section.key];
          return (
            <div key={section.key} style={{
              backgroundColor: cardBg, borderRadius: '12px', marginBottom: '8px',
              border: `1px solid ${borderColor}`, overflow: 'hidden',
            }}>
              <div
                onClick={() => toggle(section.key)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '14px 20px', cursor: 'pointer',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '32px', height: '32px', borderRadius: '8px',
                    backgroundColor: section.color + '20', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <Icon size={16} style={{ color: section.color }} />
                  </div>
                  <span style={{ fontSize: '0.95rem', fontWeight: '600', color: textColor }}>
                    {section.title}
                  </span>
                </div>
                {isOpen ? <ChevronUp size={18} color={mutedColor} /> : <ChevronDown size={18} color={mutedColor} />}
              </div>
              {isOpen && (
                <div style={{
                  padding: '0 20px 16px',
                  borderTop: `1px solid ${borderColor}`,
                  paddingTop: '14px',
                }}>
                  <p style={{
                    color: darkMode ? 'rgba(255,255,255,0.8)' : '#374151',
                    fontSize: '0.9rem', lineHeight: '1.6', margin: 0,
                  }}>
                    {section.desc}
                  </p>
                </div>
              )}
            </div>
          );
        })}

        {/* Download Full Manual */}
        <div style={{ marginTop: '16px', marginBottom: '16px' }}>
          <a
            href="/LYT_HSE_Manual_v2.3.pdf"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
              width: '100%', padding: '14px', borderRadius: '12px',
              backgroundColor: tealColor, color: '#fff', textDecoration: 'none',
              fontSize: '1rem', fontWeight: '600',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            }}
          >
            <Download size={18} /> {t('safety_download')}
          </a>
        </div>

        {/* Scope of Work */}
        <div style={{
          backgroundColor: cardBg, borderRadius: '12px', padding: '20px',
          border: `1px solid ${borderColor}`, marginBottom: '24px',
        }}>
          <h3 style={{
            fontSize: '1rem', fontWeight: '700', color: textColor, margin: '0 0 14px',
            display: 'flex', alignItems: 'center', gap: '8px',
          }}>
            <FileText size={18} style={{ color: accentPrimary }} />
            {t('safety_scope')}
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {[
              t('safety_scope_boring'),
              t('safety_scope_pulling'),
              t('safety_scope_fixtures'),
              t('safety_scope_splicing'),
              t('safety_scope_restoration'),
            ].map((item, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'flex-start', gap: '10px',
                padding: '10px 14px', borderRadius: '8px',
                backgroundColor: darkMode ? '#131f30' : '#f0f4f8',
              }}>
                <div style={{
                  width: '6px', height: '6px', borderRadius: '50%',
                  backgroundColor: accentPrimary, marginTop: '7px', flexShrink: 0,
                }} />
                <span style={{ fontSize: '0.85rem', color: textColor, lineHeight: '1.5' }}>
                  {item}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Branding */}
        <div style={{ textAlign: 'center', fontSize: '0.75rem', color: mutedColor, marginBottom: '20px' }}>
          LYT Communications &mdash; HSE Manual v2.3
        </div>
      </div>
    </div>
  );
};

export default SafetyPage;
